/**
 * Central Icon Registry for Navigation
 *
 * This registry provides a single source of truth for all navigation icons.
 * All navigation icons must be referenced by key from this registry.
 *
 * Icons are generic, role-appropriate, and framework-agnostic.
 * They do not encode meaning beyond visual identification.
 */

import {
  // Dashboard & Overview
  BarChart3,
  LayoutDashboard,

  // Platform Management
  Building2,
  Users,
  Shield,
  Clock,

  // Standards & Frameworks
  FileText,
  Layers,
  Link,
  Upload,
  Library,

  // Prepopulation & Templates
  Send,
  BookOpen,
  Book,
  Mail,

  // Audit & Governance
  ClipboardList,
  Activity,
  Bot,

  // Evidence & Documents
  Folder,
  File,
  FolderSearch,

  // Tasks & Work
  CheckSquare,
  ListTodo,

  // Communication
  Bell,
  MessageSquare,

  // Support
  MessageCircle,

  // Reporting
  FileDown,
  FileBarChart,

  // Findings
  FileText as FileText2,
  AlertTriangle,

  // Certification
  Award,
  CheckCircle2,

  // General
  Settings,
  SlidersHorizontal,
  Brain,
  LogOut,
  // Additional icons for dashboard cards
  CheckCircle,
  AlertCircle,

  // Emissions / Carbon Management
  Leaf,
  Calculator,
  Target,
  Wind,
  FileSpreadsheet,

  // GDPR & Billing
  ShieldCheck,
  CreditCard,

  // UK Compliance Gap modules
  Scale,
  Siren,
  ClipboardCheck,
  TestTube2,
  FileSignature,
  Calendar,
  HandMetal,
  Eye,
  Trash2,
  UserCheck,
  ScrollText,
} from 'lucide-react';

/**
 * Icon Registry
 *
 * Maps icon keys to Lucide React icon components.
 * All navigation icons must reference keys from this registry.
 */
export const ICONS = {
  // Dashboard & Overview
  dashboard: BarChart3,
  'admin-dashboard': LayoutDashboard,
  'company-dashboard': BarChart3,
  'compliance-dashboard': BarChart3,
  'auditor-dashboard': BarChart3,

  // Platform Management
  companies: Building2,
  users: Users,
  roles: Shield,
  'access-control': Clock,
  'expiry-access': Clock,

  // Standards & Frameworks
  'framework-manager': FileText,
  'compliance-structure': Layers,
  'framework-mapping': Link,
  'standard-upload': Upload,
  'library': Library,
  'document-library': FolderSearch,

  // Prepopulation & Templates
  'framework-assignments': Send,
  guidance: BookOpen,
  pages: Book,
  'email-templates': Mail,

  // Audit & Governance
  'audit-logs': ClipboardList,
  'system-logs': Activity,
  'bot': Bot,

  // Evidence & Documents
  'evidence-library': Folder,
  'uploaded-documents': File,
  'my-uploads': Upload,
  'reference-materials': BookOpen,

  // Tasks & Work
  'assigned-pages': CheckSquare,
  'pending-evidence': ListTodo,
  'active-frameworks': FileText,

  // Communication
  notifications: Bell,
  'requests-messages': MessageSquare,
  messages: MessageSquare,

  // Support
  feedback: MessageCircle,

  // Reporting
  'assessment-summary': FileBarChart,
  'export-reports': FileDown,

  // Findings
  'notes-observations': FileText2,
  'non-conformities': AlertTriangle,

  // Certification
  'certification-dashboard': Award,
  'issue-certification': CheckCircle2,
  'surveillance-audits': ClipboardList,

  // General
  settings: Settings,
  security: Shield,
  'ai-settings': Brain,
  'system-settings': SlidersHorizontal,
  'user-profile': Users,
  'account-governance': Shield,
  logout: LogOut,

  // Additional icons for dashboard cards
  'active-frameworks-card': CheckCircle,
  'framework-assignments-card': FileBarChart,
  'pending-evidence-card': AlertCircle,
  'assessment-summary-card': CheckCircle,
  'expiry-access-card': AlertCircle,

  // Emissions / Carbon Management
  'emissions-dashboard': Leaf,
  'bulk-calculator': FileSpreadsheet,
  'reduction-targets': Target,
  'offset-credits': Wind,
  'emission-reports': FileDown,
  'factor-library': Calculator,

  // GDPR & Billing
  'gdpr': ShieldCheck,
  'billing': CreditCard,
  'system-monitor': Activity,

  // UK Compliance Gap modules
  'obligation-register': Scale,
  'incident-register': Siren,
  'onboarding': ClipboardCheck,
  'control-tests': TestTube2,
  'vendor-contracts': FileSignature,
  'statutory-filings': Calendar,
  'modern-slavery': HandMetal,
  'privacy-reviews': Eye,
  'data-retention': Trash2,
  'policy-attestations': UserCheck,
  'tenant-audit-logs': ScrollText,
} as const;

/**
 * Icon Key Type
 *
 * Union type of all icon keys in the registry.
 */
export type IconKey = keyof typeof ICONS;

/**
 * Get Icon Component
 *
 * Returns the icon component for a given key.
 * Returns undefined if the key is not found.
 */
export function getIcon(key: IconKey) {
  return ICONS[key];
}

/**
 * Get All Icon Keys
 *
 * Returns an array of all available icon keys.
 */
export function getAllIconKeys(): IconKey[] {
  return Object.keys(ICONS) as IconKey[];
}
